<template>
  <div>
    <div class="section-intro">
      <h2><svg class="icon" aria-hidden="true"><use href="#i-card"/></svg> {{ $t('intake.portal.payIntro') }}</h2>
      <p>{{ $t('intake.portal.payIntroBody') }}</p>
    </div>

    <p v-if="needsLogin" class="empty" role="status">
      {{ $t('intake.portal.needsLogin') }}
      <router-link class="btn btn-ghost" :to="{name: 'login', params: {lang}}">{{ $t('intake.portal.needsLoginCta') }}</router-link>
    </p>

    <div v-else-if="loading" class="empty"><p>{{ $t('intake.portal.loading') }}</p></div>

    <template v-else>
      <booking-recovery-notice :lang="lang" @change="bookingRecovery = $event" />
      <p class="payment-state-explanation">{{ copy.invoiceNotice }}</p>
      <div class="metric-grid">
        <article class="metric">
          <span>{{ $t('intake.portal.payCreditLabel') }}</span>
          <b>{{ creditLabel }}</b>
          <router-link v-if="!bookingRecovery.blocksNewBooking" class="btn btn-ghost" :to="creditCtaTo">{{ $t('intake.portal.payCreditCta') }}</router-link>
        </article>
        <article v-if="currentPage === 1 && !error" class="metric">
          <span>{{ $t('intake.portal.payLastLabel') }}</span>
          <b>{{ lastStatus }}</b>
          <small>{{ lastMeta }}</small>
        </article>
      </div>
      <div v-if="!bookingRecovery.blocksNewBooking" class="package-cta">
        <router-link class="btn btn-primary" :to="r('portalPackage')">{{ $t('intake.portal.payBuyPackage') }}</router-link>
      </div>

      <div v-if="error" class="empty" role="alert">
        <p>{{ error }}</p><button type="button" class="btn btn-secondary" @click="bootstrap">{{ copy.retry }}</button>
      </div>
      <div v-else-if="factors.length" class="list">
        <article v-for="factor in factors" :key="factor.id" class="list-card">
          <div class="copy">
            <h3>{{ factorHeading(factor) }}</h3>
            <p>{{ factorMeta(factor) }}</p>
          </div>
          <div class="list-actions">
            <router-link class="btn btn-secondary btn-sm" :to="receiptLink(factor)">{{ $t('intake.portal.payReceiptCta') }}</router-link>
          </div>
        </article>
      </div>
      <div v-else class="empty">
        <h3>{{ $t('intake.portal.payEmptyTitle') }}</h3>
        <p>{{ $t('intake.portal.payEmptyBody') }}</p>
      </div>
      <nav v-if="!error && (totalPages > 1 || currentPage > 1)" class="actions history-pagination" :aria-label="copy.pagination">
        <button type="button" class="btn btn-secondary btn-sm" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">{{ copy.previous }}</button>
        <span role="status">{{ pageLabel }}</span>
        <button type="button" class="btn btn-secondary btn-sm" :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">{{ copy.next }}</button>
      </nav>
    </template>
  </div>
</template>
<script>
import BookingRecoveryNotice from '@/components/portal/BookingRecoveryNotice.vue'
import {
  factorStateLabel,
  factorTitle,
  fetchActiveCare,
  fetchProfile,
  fetchUserFactorPage,
  formatFactorDate,
  formatMoneyAmount,
  getCachedProfile,
  hasAuthToken,
  reserveSessionRoute,
  toFaDigits,
  walletSessionCredit,
} from '@/intake/portalApi'

export default {
  name: 'PortalPaymentsStep',
  components: {BookingRecoveryNotice},
  data() {
    return {
      loading: true,
      needsLogin: false,
      profile: getCachedProfile(),
      factors: [],
      error: '',
      pagination: {current_page: 1, total_pages: 1},
      generation: 0,
      bookingRecovery: {status: 'LOADING', blocksNewBooking: true},
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
    currentPage() {
      const raw = this.$route.query.page
      const value = Number(Array.isArray(raw) ? raw[0] : raw)
      return Number.isSafeInteger(value) && value > 0 ? value : 1
    },
    totalPages() { return this.pagination.total_pages || 1 },
    copy() {
      return this.lang === 'fa'
        ? {retry:'تلاش دوباره',loadError:'دریافت سابقهٔ پرداخت انجام نشد. دوباره تلاش کنید.',previous:'صفحهٔ قبل',next:'صفحهٔ بعد',pagination:'صفحه‌بندی سابقهٔ پرداخت',invoiceNotice:'ثبت فاکتور به معنی پرداخت یا رزرو قطعی جلسه نیست. وضعیت «پرداخت‌شده» مربوط به فاکتور است؛ جلسهٔ قطعی در بخش جلسات نمایش داده می‌شود. اگر مبلغی کسر شده ولی پرداخت تأیید نشده، پیش از پرداخت دوباره رسید بانکی را با پشتیبانی بررسی کنید.'}
        : {retry:'Try again',loadError:'Payment history could not be loaded. Please try again.',previous:'Previous page',next:'Next page',pagination:'Payment history pages',invoiceNotice:'Submitting an invoice does not confirm payment or a session. “Paid” describes the invoice; confirmed appointments appear under Sessions. If your account was charged but payment is unconfirmed, ask support to review the bank receipt before paying again.'}
    },
    pageLabel() {
      return this.lang === 'fa' ? `صفحهٔ ${toFaDigits(this.currentPage)} از ${toFaDigits(this.totalPages)}` : `Page ${this.currentPage} of ${this.totalPages}`
    },
    creditCount() {
      return walletSessionCredit(this.profile)
    },
    creditLabel() {
      const n = this.creditCount
      if (this.lang === 'fa') {
        return `${toFaDigits(n)} جلسه`
      }
      return n === 1 ? '1 session' : `${n} sessions`
    },
    creditCtaTo() {
      return this.creditCount > 0 ? reserveSessionRoute(this.lang) : this.r('portalPackage')
    },
    lastFactor() {
      return this.currentPage === 1 && !this.error ? this.factors[0] || null : null
    },
    lastStatus() {
      if (!this.lastFactor) return this.$t('intake.portal.payLastNone')
      return factorStateLabel(this.lastFactor, this.lang)
    },
    lastMeta() {
      if (!this.lastFactor) return this.$t('intake.portal.payLastNoneMeta')
      const amount = formatMoneyAmount(
          this.lastFactor.price,
          this.lastFactor.currency_code || this.lastFactor.currency,
          this.lang,
      )
      const date = formatFactorDate(this.lastFactor, this.lang)
      return [amount, date].filter(Boolean).join(' · ')
    },
  },
  mounted() {
    if (this.$route.query.next_session_hold) {
      return fetchActiveCare().then((episode) => {
        if (episode && episode.id) return this.$router.replace({
          name: 'portalNextSessionPayReturn', params: {lang: this.lang},
          query: {episode_id: episode.id, hold_id: this.$route.query.next_session_hold},
        })
        return this.bootstrap()
      }).catch(() => this.bootstrap())
    }
    return this.bootstrap()
  },
  watch: {
    '$route.query.page'() { if (!this.$route.query.next_session_hold) return this.bootstrap() },
  },
  beforeDestroy() { this.generation += 1 },
  methods: {
    r(name) {
      return {name, params: {lang: this.lang, therapistId: 't1'}}
    },
    receiptLink(factor) {
      return {
        name: 'portalReceipt',
        params: {lang: this.lang},
        query: {id: String(factor.id), ...(this.currentPage > 1 ? {history_page: String(this.currentPage)} : {})},
      }
    },
    pageLink(page) {
      const query = {...this.$route.query}
      if (page > 1) query.page = String(page)
      else delete query.page
      return {name:'portalPayments', params:{lang:this.lang}, query, hash:this.$route.hash}
    },
    goToPage(page) {
      if (this.loading || page === this.currentPage || page < 1 || page > this.totalPages) return
      return this.$router.push(this.pageLink(page))
    },
    factorHeading(factor) {
      return factorTitle(factor, this.lang)
    },
    factorMeta(factor) {
      const state = factorStateLabel(factor, this.lang)
      const date = formatFactorDate(factor, this.lang)
      const amount = formatMoneyAmount(factor.price, factor.currency_code || factor.currency, this.lang)
      return [date, amount, state].filter(Boolean).join(' · ')
    },
    bootstrap() {
      const generation = ++this.generation
      this.loading = true
      this.error = ''
      this.factors = []
      this.needsLogin = !hasAuthToken()
      if (this.needsLogin) {
        this.loading = false
        return
      }
      return Promise.all([
        fetchProfile().catch(() => getCachedProfile()),
        fetchUserFactorPage(this.currentPage),
      ])
          .then(([profile, history]) => {
            if (generation !== this.generation) return
            if (this.currentPage > history.pagination.total_pages) return this.$router.replace(this.pageLink(history.pagination.total_pages))
            this.profile = profile
            this.factors = history.factors
            this.pagination = history.pagination
          })
          .catch(() => {
            if (generation === this.generation) this.error = this.copy.loadError
          })
          .finally(() => {
            if (generation === this.generation) this.loading = false
          })
    },
  },
}
</script>
<style scoped>
.history-pagination { align-items: center; justify-content: center; margin-block-start: 1.5rem; }
.history-pagination button:disabled { opacity: .5; cursor: default; }
</style>
